import React, { useCallback, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import PropTypes from 'prop-types';

import { useMask } from '@atlas/react-forms';
import { FieldHint, Input } from '@atlas/ui';

import Field from 'common/Form/Field';

const NumberField = ({ field, onBlur, onChange, onFocus }) => {
  const {
    active,
    amountIcon,
    autocomplete,
    autofocus,
    className,
    disabled,
    errors,
    hint,
    isModifiable = true,
    label,
    maskOption,
    name,
    placeholder,
    touched,
    uiTitleClassName,
    value,
  } = field;
  const {
    handleBeforeInput,
    handleChange,
    handleKeyDown,
    maskedValue,
    value: cleanValue,
  } = useMask({
    digitsOnly: true,
    mask: maskOption,
  });

  const changeHandler = useCallback(
    event => {
      const { value } = event.target;
      const formattedValue = value === '' ? null : Number(value);

      onChange(formattedValue);
    },
    [onChange],
  );

  const error = touched && errors[0];

  useEffect(() => {
    if (value && String(value) !== cleanValue) {
      handleChange({
        target: {
          selectionStart: String(value).length,
          value: String(value),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChange, maskOption, value]);

  useEffect(() => {
    if (maskOption) {
      const formattedValue = cleanValue === '' ? null : Number(cleanValue);
      onChange(formattedValue);
    }
  }, [cleanValue, maskOption, onChange]);

  const success = touched && !error;
  const isDisabled = disabled || !isModifiable;

  if (maskOption) {
    return (
      <Field options={field}>
        <Input
          active={active}
          amountIcon={amountIcon}
          autoFocus={autofocus}
          className={className}
          disabled={isDisabled}
          error={error}
          label={label}
          name={name}
          onBeforeInput={handleBeforeInput}
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          placeholder={maskOption}
          success={success}
          touched={touched}
          uiTitleClassName={uiTitleClassName}
          value={maskedValue}
        />
        {!isDisabled && <FieldHint hint={hint} showOnClick />}
      </Field>
    );
  }

  return (
    <Field options={field}>
      <NumericFormat
        active={active}
        amountIcon={amountIcon}
        autoComplete={autocomplete}
        autoFocus={autofocus}
        className={className}
        customInput={Input}
        disabled={isDisabled}
        label={label}
        name={name}
        onBlur={onBlur}
        onChange={changeHandler}
        onFocus={onFocus}
        placeholder={placeholder}
        success={success}
        touched={touched}
        uiTitleClassName={uiTitleClassName}
        value={value}
      />
      {!isDisabled && <FieldHint hint={hint} />}
    </Field>
  );
};

NumberField.propTypes = {
  field: PropTypes.shape({
    active: PropTypes.bool,
    amount: PropTypes.string,
    amountIcon: PropTypes.string,
    autoComplete: PropTypes.string,
    autofocus: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    errors: PropTypes.arrayOf(PropTypes.object),
    hint: PropTypes.string,
    isModifiable: PropTypes.bool,
    label: PropTypes.string,
    maskOption: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    touched: PropTypes.bool,
    uiTitleClassName: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default NumberField;
